import type { CSSProperties } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { count, periodNoun } from "@volca/ui/trades";
import { dayjs } from "@/core/utils/date";
import { MetaList } from "@/profitability/meta-list";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import { flagColumn } from "@/profitability/records/objects";
import type { RecordList } from "@/profitability/records/record-panel";
import { SectionHeading } from "@/profitability/records/section-heading";
import type { ReportDates, ReportRow } from "@/profitability/types";
import { PeriodDay, PERIOD_BAR_HEIGHT } from "./period-day";

const MAX_COLUMNS = 8;
interface Day {
  date: dayjs.Dayjs;
  rows: ReportRow[];
}

/** All volumes share a day list; narrow charts use horizontal day bars. */
export const PeriodStrip = ({
  dates,
  object,
  onOpenRecord,
}: {
  dates: ReportDates;
  object: RecordObject;
  onOpenRecord: (ref: RecordRef, list?: RecordList | null) => void;
}) => {
  const field = object.dataset.period_field;
  if (!field) return null;
  const flag = flagColumn(object.dataset);
  const start = dayjs(dates.start_date);
  const length = dayjs(dates.end_date).diff(start, "day") + 1;
  const byWeek = length > MAX_COLUMNS;
  const columns: Day[] = Array.from(
    { length: byWeek ? Math.ceil(length / 7) : length },
    (_, index) => ({
      date: start.add(index * (byWeek ? 7 : 1), "day"),
      rows: [],
    }),
  );
  for (const row of object.dataset.rows) {
    const at = dayjs(String(row[field] ?? ""));
    if (!at.isValid()) continue;
    const offset = at.diff(start, "day");
    columns[byWeek ? Math.floor(offset / 7) : offset]?.rows.push(row);
  }
  const flagged = flag
    ? object.dataset.rows.filter((row) => row[flag.key]).length
    : 0;
  const busiest = Math.max(1, ...columns.map((column) => column.rows.length));
  return (
    <Stack
      render={<section />}
      gap={3}
      className="@container/week w-full min-w-0"
      aria-label={`The period's ${object.plural.toLowerCase()}`}
    >
      <SectionHeading
        action={
          <MetaList className="text-xs text-subtle tabular-nums">
            {count(
              object.dataset.rows.length,
              object.name.toLowerCase(),
              object.plural.toLowerCase(),
            )}
            {flagged > 0 && (
              <span className="text-warning">{flagged} flagged</span>
            )}
          </MetaList>
        }
      >
        The {periodNoun(length)}
      </SectionHeading>
      <Stack
        className="grid min-w-0 grid-cols-1 gap-1 @md/week:grid-cols-[repeat(var(--period-columns),minmax(0,1fr))]"
        style={{ "--period-columns": columns.length } as CSSProperties}
      >
        {columns.map((column) => (
          <PeriodDay
            key={column.date.format("YYYY-MM-DD")}
            date={column.date}
            byWeek={byWeek}
            object={object}
            rows={column.rows}
            height={Math.round(
              (column.rows.length / busiest) * PERIOD_BAR_HEIGHT,
            )}
            onOpenRecord={onOpenRecord}
          />
        ))}
      </Stack>
    </Stack>
  );
};
